import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Skeleton } from "../components/Loader";
import DatePicker from "react-datepicker";
import { useDispatch, useSelector } from "react-redux";
import { useSearchParams } from "react-router-dom";
import {
  CartesianGrid,
  Cell,
  Legend,
  Line,
  LineChart,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import {
  CHART_SERIES,
  OTHER_COLOR,
} from "../shared/chartTheme";

import {
  fetchHomeReport,
  selectHomeData,
  selectHomeError,
  selectHomeStatus,
} from "../features/reports/reportsSlice";
import { fetchStores, selectStores, selectStoresStatus } from "../features/stores/storesSlice";
import StoreMultiSelect from "../components/StoreMultiSelect";

import "./ReportPage.css";

/**
 * Главная страница CRM — замена дашборда Metabase `5-auf-home-page` (AUFB-65).
 *
 * Оперативная сводка за день: открытые смены, смены на плановом среднем чеке,
 * продажи по способам оплаты, таблица смен, рейтинг товаров и график по часам.
 * Данные — `/api/reports/home/`.
 */

const PAYMENT_TYPE_LABELS = {
  CASH: "Наличные",
  CARD: "Карта",
  QR: "QR",
  ONLINE: "Онлайн",
  NONE: "Без оплаты",
};

const SHIFT_STATUS_LABELS = {
  open: "Открыта",
  closed: "Закрыта",
};

const CHART_COLORS = CHART_SERIES;

function formatMoney(value) {
  const num = Number(value || 0);
  if (Number.isNaN(num)) return "0.00";
  return num.toLocaleString("ru-RU", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

function formatMoneyShort(value) {
  const num = Number(value || 0);
  if (Number.isNaN(num)) return "0";
  return num.toLocaleString("ru-RU", { maximumFractionDigits: 0 });
}

function formatCount(value) {
  const num = Number(value || 0);
  if (Number.isNaN(num)) return "0";
  return num.toLocaleString("ru-RU");
}

function formatQuantity(value) {
  const num = Number(value || 0);
  if (Number.isNaN(num)) return "0";
  return Number.isInteger(num) ? num.toLocaleString("ru-RU") : num.toLocaleString("ru-RU", { maximumFractionDigits: 3 });
}

function formatAxisMoney(v) {
  const n = Number(v);
  if (!Number.isFinite(n)) return "";
  if (Math.abs(n) >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`;
  if (Math.abs(n) >= 10_000) return `${(n / 1000).toFixed(0)}k`;
  return String(Math.round(n));
}

/** Ось графика — только час: вся серия внутри одного дня. */
function formatHour(v) {
  if (!v) return "";
  const d = new Date(v);
  if (Number.isNaN(d.getTime())) return String(v);
  return `${String(d.getHours()).padStart(2, "0")}:00`;
}

function formatDateTime(v) {
  if (!v) return "—";
  const d = new Date(v);
  if (Number.isNaN(d.getTime())) return String(v);
  return d.toLocaleString("ru-RU", {
    day: "2-digit",
    month: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
  });
}

function toIsoLocal(d) {
  if (!d) return "";
  const year = d.getFullYear();
  const month = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
}

function parseIsoLocal(iso) {
  if (!iso || typeof iso !== "string") return null;
  const m = iso.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (!m) return null;
  const y = Number(m[1]);
  const mo = Number(m[2]);
  const d = Number(m[3]);
  if (!y || !mo || !d) return null;
  return new Date(y, mo - 1, d);
}

function todayIso() {
  return toIsoLocal(new Date());
}

function KpiCard({ title, value, hint, accent }) {
  return (
    <div className="kpi-card">
      <div className="kpi-title">{title}</div>
      <div className={accent ? `kpi-value ${accent}` : "kpi-value"}>{value}</div>
      {hint && <div className="kpi-hint">{hint}</div>}
    </div>
  );
}

function Section({ title, subtitle, children }) {
  return (
    <div className="section">
      <div className="section-head">
        <div className="section-title">{title}</div>
        {subtitle && <div className="section-subtitle">{subtitle}</div>}
      </div>
      {children}
    </div>
  );
}

function Empty({ text = "Нет данных за день" }) {
  return <div className="empty">{text}</div>;
}

function BreakdownTable({ rows, columns, emptyText, footer }) {
  if (!rows || rows.length === 0) return <Empty text={emptyText} />;
  return (
    <div className="table-wrap">
      <table>
        <thead>
          <tr>
            {columns.map((col) => (
              <th key={col.key} className={col.numeric ? "numeric" : undefined}>
                {col.title}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((row, index) => (
            <tr key={row.id ?? index}>
              {columns.map((col) => {
                const raw = row[col.key];
                let content = raw;
                if (col.render) content = col.render(row);
                else if (col.money) content = formatMoney(raw);
                else if (col.quantity) content = formatQuantity(raw);
                else if (col.numeric) content = formatCount(raw);
                else if (raw === null || raw === undefined || raw === "") content = "—";
                const cellClasses = [col.numeric ? "numeric" : "", col.bold ? "bold" : ""].filter(Boolean).join(" ");
                return (
                  <td key={col.key} className={cellClasses || undefined}>
                    {content}
                  </td>
                );
              })}
            </tr>
          ))}
        </tbody>
      </table>
      {footer && <div className="hint-block">{footer}</div>}
    </div>
  );
}

const tooltipStyle = {
  background: "var(--c-card)",
  border: "1px solid var(--c-border)",
  borderRadius: 8,
  color: "var(--c-text)",
  fontSize: 14,
};

export default function HomePage() {
  const dispatch = useDispatch();
  const [searchParams, setSearchParams] = useSearchParams();

  const stores = useSelector(selectStores);
  const storesStatus = useSelector(selectStoresStatus);

  const data = useSelector(selectHomeData);
  const status = useSelector(selectHomeStatus);
  const error = useSelector(selectHomeError);

  const [storeIds, setStoreIds] = useState(() => {
    const raw = searchParams.getAll("store_ids");
    return raw.length ? raw.map(String) : [];
  });
  const [day, setDay] = useState(() => searchParams.get("day") || todayIso());

  useEffect(() => {
    if (storesStatus === "idle") dispatch(fetchStores({ pageSize: 200 }));
  }, [dispatch, storesStatus]);

  const load = useCallback(() => {
    const numericIds = storeIds.map((id) => Number(id)).filter((id) => Number.isFinite(id));
    dispatch(fetchHomeReport({ storeIds: numericIds, day, topProducts: 15 }));
  }, [dispatch, storeIds, day]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    const params = new URLSearchParams();
    storeIds.forEach((id) => params.append("store_ids", String(id)));
    if (day) params.set("day", day);
    setSearchParams(params, { replace: true });
  }, [storeIds, day, setSearchParams]);

  const handleReset = () => {
    setStoreIds([]);
    setDay(todayIso());
  };

  const totals = data?.totals;
  const shifts = data?.shifts;
  const topProducts = data?.top_products;

  const paymentRows = useMemo(
    () =>
      (data?.by_payment_type || []).map((row) => ({
        ...row,
        id: row.payment_type,
        label: PAYMENT_TYPE_LABELS[row.payment_type] || row.payment_type || "—",
        amount_num: Math.abs(Number(row.amount || 0)),
      })),
    [data]
  );

  const paymentTotal = useMemo(
    () => paymentRows.reduce((acc, row) => acc + row.amount_num, 0),
    [paymentRows]
  );

  const shiftRows = useMemo(
    () => (shifts?.items || []).map((row, i) => ({ ...row, id: row.shift_id ?? i })),
    [shifts]
  );

  const productRows = useMemo(
    () => (topProducts?.items || []).map((row, i) => ({ ...row, id: row.product_id ?? i })),
    [topProducts]
  );

  const productDonut = useMemo(() => {
    const rows = (topProducts?.items || []).map((item) => ({
      name: item.product_name,
      value: Number(item.quantity || 0),
    }));
    const other = Number(topProducts?.other?.quantity || 0);
    if (other > 0) rows.push({ name: "Другое", value: other, isOther: true });
    return rows;
  }, [topProducts]);

  const hourlyRows = useMemo(
    () => (data?.hourly || []).map((row) => ({ ...row, amount_num: Number(row.amount || 0) })),
    [data]
  );

  const productTotal = Number(topProducts?.total_quantity || 0);

  return (
    <div className="sales-dashboard-page">
      <div className="header">
        <div>
          <div className="title">Главная</div>
          <div className="subtitle">Смены и продажи за день</div>
        </div>
      </div>

      <div className="toolbar">
        <StoreMultiSelect stores={stores} value={storeIds} onChange={setStoreIds} />
        <div className="field">
          <label className="label">Дата</label>
          <DatePicker
            selected={parseIsoLocal(day)}
            onChange={(d) => setDay(toIsoLocal(d))}
            dateFormat="dd.MM.yyyy"
            placeholderText="дд.мм.гггг"
            customInput={<input className="input" />}
            wrapperClassName="pnl-datepicker-wrapper"
          />
        </div>
        <div className="toolbar-buttons">
          <button type="button" onClick={load} className="apply-btn">
            Обновить
          </button>
          <button type="button" onClick={handleReset} className="reset-btn">
            Сегодня
          </button>
        </div>
      </div>

      {status === "loading" && <Skeleton variant="cards" count={4} text="Загрузка сводки…" style={{ padding: "16px 24px 32px" }} />}
      {status === "failed" && error && <div className="error-box">{error}</div>}

      <div className="body">
        <div className="kpi-grid">
          <KpiCard
            title="Открытых смен"
            value={formatCount(shifts?.open_count)}
            hint={`всего смен за день: ${formatCount(shifts?.total_count)}`}
          />
          <KpiCard
            title="Смен достигших среднего"
            value={formatCount(shifts?.reached_plan_count)}
            accent={shifts?.reached_plan_count ? "accent" : undefined}
            hint="средний чек не ниже плана магазина"
          />
          <KpiCard title="Общие продажи" value={formatMoneyShort(totals?.net_revenue)} accent="accent" />
          <KpiCard
            title="Средняя сумма продаж"
            value={formatMoney(totals?.avg_check)}
            hint={`${formatCount(totals?.sales_count)} чеков`}
          />
        </div>

        <div className="two-col">
          <Section title="Общие продажи по способу оплаты">
            {paymentRows.length === 0 ? (
              <Empty />
            ) : (
              <ResponsiveContainer width="100%" height={300}>
                <PieChart>
                  <Pie
                    data={paymentRows}
                    dataKey="amount_num"
                    nameKey="label"
                    innerRadius={70}
                    outerRadius={110}
                    paddingAngle={2}
                  >
                    {paymentRows.map((row, index) => (
                      <Cell key={row.id} fill={CHART_COLORS[index % CHART_COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip
                    contentStyle={tooltipStyle}
                    formatter={(value, name) => {
                      const share = paymentTotal ? ((Number(value) / paymentTotal) * 100).toFixed(2) : "0.00";
                      return [`${formatMoney(value)} (${share}%)`, name];
                    }}
                  />
                  <Legend wrapperStyle={{ fontSize: 14 }} />
                </PieChart>
              </ResponsiveContainer>
            )}
          </Section>

          <Section title="Проданные товары" subtitle={`Всего ${formatQuantity(topProducts?.total_quantity)} шт.`}>
            {productDonut.length === 0 ? (
              <Empty />
            ) : (
              <ResponsiveContainer width="100%" height={300}>
                <PieChart>
                  <Pie
                    data={productDonut}
                    dataKey="value"
                    nameKey="name"
                    innerRadius={70}
                    outerRadius={110}
                    paddingAngle={2}
                  >
                    {productDonut.map((row, index) => (
                      <Cell
                        key={row.name}
                        fill={row.isOther ? OTHER_COLOR : CHART_COLORS[index % CHART_COLORS.length]}
                      />
                    ))}
                  </Pie>
                  <Tooltip
                    contentStyle={tooltipStyle}
                    formatter={(value, name) => {
                      const share = productTotal ? ((Number(value) / productTotal) * 100).toFixed(2) : "0.00";
                      return [`${formatQuantity(value)} (${share}%)`, name];
                    }}
                  />
                  <Legend wrapperStyle={{ fontSize: 14 }} />
                </PieChart>
              </ResponsiveContainer>
            )}
          </Section>
        </div>

        <Section title="Продажи по сменам">
          <BreakdownTable
            rows={shiftRows}
            columns={[
              { key: "opened_at", title: "Время открытия", render: (row) => formatDateTime(row.opened_at) },
              { key: "shift_id", title: "ID смены", numeric: true },
              { key: "store", title: "Магазин" },
              { key: "cashier", title: "Кассир" },
              {
                key: "status",
                title: "Статус",
                render: (row) => SHIFT_STATUS_LABELS[row.status] || row.status || "—",
              },
              { key: "avg_check", title: "Средний чек", numeric: true, money: true },
              { key: "total_sales", title: "Сумма продаж", numeric: true, money: true, bold: true },
              { key: "opening_amount", title: "Сумма открытия", numeric: true, money: true },
            ]}
            footer={`${shiftRows.length} строк`}
            emptyText="Смен за этот день нет"
          />
        </Section>

        <Section title="Рейтинг проданных товаров">
          <BreakdownTable
            rows={productRows}
            columns={[
              { key: "product_name", title: "Название продукта" },
              { key: "barcode", title: "Barcode" },
              { key: "quantity", title: "Количество", numeric: true, quantity: true },
              { key: "amount", title: "Сумма", numeric: true, money: true, bold: true },
            ]}
            footer={`Показано ${productRows.length} из ${formatCount(topProducts?.total_positions)} позиций`}
            emptyText="За день не продано ни одного товара"
          />
        </Section>

        <Section title="График продаж" subtitle="По часам">
          {hourlyRows.length === 0 ? (
            <Empty />
          ) : (
            <ResponsiveContainer width="100%" height={320}>
              <LineChart data={hourlyRows} margin={{ top: 8, right: 16, bottom: 8, left: 8 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="var(--c-border)" />
                <XAxis dataKey="hour" tickFormatter={formatHour} stroke="var(--c-text-muted)" fontSize={12} />
                <YAxis tickFormatter={formatAxisMoney} stroke="var(--c-text-muted)" fontSize={12} />
                <Tooltip
                  contentStyle={tooltipStyle}
                  labelFormatter={formatHour}
                  formatter={(value) => [formatMoney(value), "Продажи"]}
                />
                <Line
                  type="monotone"
                  dataKey="amount_num"
                  name="Продажи"
                  stroke={CHART_COLORS[0]}
                  strokeWidth={2}
                  dot={{ r: 3 }}
                />
              </LineChart>
            </ResponsiveContainer>
          )}
        </Section>
      </div>
    </div>
  );
}
